/**
 * The browser's record (#788 PR2): what only the page can see, sent only if
 * they agreed to it, to our own server (/api/record) and from there into the
 * events bucket beside the server's own (lib/platform/track.ts).
 *
 * Nothing leaves before `recording(true)`: no queue fills, no request is
 * made. The session is a random word kept in this module — not in a cookie,
 * not in storage — and replaced after half an hour without anything
 * happening, the same cut the access log makes (lib/domain/visits.ts).
 *
 * The session word is the browser's, not a person's: the server knows who
 * sent each batch (the cookie), the page does not — another tab may have
 * signed somebody else in — so the server writes the session out as that
 * person's (lib/platform/track.ts `sessionOf`), and one word never joins two
 * people (Issue 804).
 *
 * Fire-and-forget: a batch every ten seconds or twenty events, and what is
 * left when the tab is hidden or closed goes with `sendBeacon`, which the
 * browser delivers after the page is gone. Losing one is not worth a retry —
 * except at signing out, when what waits is handed over first (`handOver`).
 *
 * Imports nothing that is not the browser's (tests/guards.test.ts).
 */
export type BrowserEvent =
  | { name: "page_viewed"; props: { route: string; nav: "hard" | "soft" | "back" | "return"; from?: string; step?: number } }
  | { name: "page_left"; props: { route: string; visible_ms: number; step?: number } }
  | { name: "ui_action"; props: { surface: string; action: string; target?: string } }
  | { name: "client_error"; props: { surface: string; kind: "write" | "nav" | "render" | "upload" | "export" | "chat" | "uncaught" | "request"; code?: string; status?: number } }
  | { name: "filter_used"; props: { use: "filter" | "sort" | "tag" | "search"; type?: string; page: "jobs" | "companies" } };

export const ENDPOINT = "/api/record";
export const BATCH = 20;
export const EVERY_MS = 10_000;
export const SESSION_GAP_MS = 30 * 60_000;
/** Longest a page may be told as having been in front of them (the server's
 *  limit for `visible_ms`). */
const MAX_VISIBLE_MS = 86_400_000;
/** Longest signing out waits for what is queued to be delivered: only when
 *  the record's own request hangs (`handOver`). */
export const HAND_OVER_MS = 2_000;

/** A word the server will take (lib/platform/events-catalog.ts WORD). What
 *  is not one is dropped here rather than sent to be dropped there. */
export const WORD = /^[a-z0-9][a-z0-9_.:-]{0,39}$/;

type Queued = { name: BrowserEvent["name"]; props: BrowserEvent["props"]; at: number };

type State = {
  on: boolean;
  /** Signing out: nothing more is taken until the page is gone, or stays. */
  held: boolean;
  queue: Queued[];
  session: string;
  last: number;
  timer: ReturnType<typeof setTimeout> | null;
  /** Whatever it returns is waited for, if it is a promise (`handOver`). */
  send: (body: string, leaving: boolean) => unknown;
  now: () => number;
};

function newSession(): string {
  const bytes = new Uint8Array(8);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/** Settles when the request has (a beacon: at once). */
function defaultSend(body: string, leaving: boolean): Promise<unknown> | void {
  try {
    if (leaving && typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: "application/json" }));
      return;
    }
    // The address written out, not ENDPOINT: tests/billing.test.ts reads it.
    return fetch("/api/record", { method: "POST", headers: { "content-type": "application/json" }, body, keepalive: true }).catch(() => {});
  } catch {
    /* a record that could break a page is worse than none */
  }
}

const state: State = { on: false, held: false, queue: [], session: "", last: 0, timer: null, send: defaultSend, now: () => Date.now() };

/** Tests only: what sends, and what time it is. */
export function wire(over: Partial<Pick<State, "send" | "now">>): void {
  Object.assign(state, over);
}

/** Their choice in this browser. Off drops what is queued, unsent — and the
 *  showing with it: its `page_viewed` may have been in what was dropped, and
 *  time after they said no is not counted. Said yes again, the page is a new
 *  showing (`agreed`). */
export function recording(on: boolean): void {
  state.on = on;
  if (!on) {
    state.queue.length = 0;
    if (state.timer) clearTimeout(state.timer);
    state.timer = null;
    if (doc.shown) restart(doc.shown);
  }
}

export function isRecording(): boolean {
  return state.on;
}

/** One thing seen. Never throws, never waits. */
export function record<E extends BrowserEvent>(name: E["name"], props: E["props"]): void {
  if (!state.on || state.held) return;
  props = tidy(props);
  const now = state.now();
  if (!state.session || now - state.last > SESSION_GAP_MS) state.session = newSession();
  state.last = now;
  state.queue.push({ name, props, at: now });
  if (state.queue.length >= BATCH) flush(false);
  else if (!state.timer) state.timer = setTimeout(() => flush(false), EVERY_MS);
}

/** What is queued, sent now. `leaving`: the tab is going, so the browser
 *  should deliver it after the page is gone. Settles when every request it
 *  made has; only `handOver` waits for that. */
export function flush(leaving: boolean): Promise<void> {
  if (state.timer) clearTimeout(state.timer);
  state.timer = null;
  const out: unknown[] = [];
  const now = state.now();
  while (state.on && state.queue.length) {
    const events = state.queue.splice(0, 50).map((e) => ({ name: e.name, props: e.props, ms_ago: Math.max(0, Math.min(600_000, now - e.at)) }));
    out.push(state.send(JSON.stringify({ session: state.session, events }), leaving));
  }
  return Promise.all(out).then(() => undefined, () => undefined);
}

/** A word-valued property that is not a word — an op name with a capital, a
 *  command the server would refuse — is left out, so the rest of the event
 *  is still taken; a page's address is never one (`route`/`from` come from
 *  routes.ts and are kept as they are). */
function tidy<P extends BrowserEvent["props"]>(props: P): P {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(props)) {
    if (v === undefined) continue;
    if (typeof v === "string" && k !== "route" && k !== "from" && !WORD.test(v)) continue;
    out[k] = v;
  }
  return out as P;
}

// ------------------------------------------------------------ arrivals
// Kept here, not in a component, because a page of one layout leading to a
// page of another unmounts one Recorder and mounts the next: the way they came
// and where from must survive that (review of PR2, warning 10).
const arrivals = { count: 0, last: null as string | null, backAt: 0 };

/** The browser went back or forward: the next arrival within a moment is one. */
export function wentBack(now = Date.now()): void {
  arrivals.backAt = now;
}

/** A page arrived: how (the first in this document is a whole load, then a
 *  move — or a step back, if the browser just went back) and from which page. */
export function arrived(route: string, now = Date.now()): { nav: "hard" | "soft" | "back"; from?: string } {
  const nav = arrivals.count++ === 0 ? "hard" : now - arrivals.backAt < 2_000 ? "back" : "soft";
  const from = arrivals.last ?? undefined;
  arrivals.last = route;
  arrivals.backAt = 0;
  return { nav, ...(from && nav !== "hard" ? { from } : {}) };
}

// ------------------------------------------------------------ the page in front of them
// Kept here for the same reason as the arrivals: a tab shows one page at a
// time, whichever Recorder happens to be mounted — one per layout, and in
// development StrictMode mounts each twice. Held in a component, the page was
// left and reached again each time one went and came (Issue 804).
//
// A showing is a stretch of time the page was in front of them: it starts
// with `page_viewed` and ends with its own `page_left`. It ends when they move
// on, and also when the tab is hidden — on a phone that is often the last
// thing a page hears before it is killed, with no `pagehide` after it. Shown
// again (back in front, or brought back from the back-forward cache), the
// same page is a new showing, `nav: "return"`. Time on one page is the sum of
// its `page_left`s; arrivals are the showings that are not returns.

type Arrival = { nav: "hard" | "soft" | "back"; from?: string };
type Shown = {
  route: string;
  step?: number;
  /** Its `page_viewed` is out, so its `page_left` is owed. */
  told: boolean;
  /** How it was reached, until a showing of it is told: a page that arrived
   *  in a hidden tab, or before they agreed, is told by it when first seen. */
  arrival?: Arrival;
  /** Since when it has been in front of them (told: since its showing
   *  started); null while it is not. */
  since: number | null;
};
const doc = { shown: null as Shown | null, recorders: 0 };

const stepOf = (s: Shown) => (s.step ? { step: s.step } : {});
const inFront = () => typeof document === "undefined" || document.visibilityState === "visible";

/** Untold again, as it stands: in front of them or not. */
function restart(s: Shown): void {
  s.told = false;
  s.since = inFront() ? state.now() : null;
}

/** The showing starts, if it can be told: in front of them, recording, and
 *  not signing out. Its time is counted from here — not from before they
 *  agreed, nor while they had said no. */
function tell(s: Shown, nav: "hard" | "return"): void {
  if (s.told || s.since === null || !state.on || state.held) return;
  s.told = true;
  s.since = state.now();
  record("page_viewed", { route: s.route, ...(s.arrival ?? { nav }), ...stepOf(s) });
  s.arrival = undefined;
}

/** The showing ends: told as left, if it was told as shown. The page stays
 *  the one in front of them, untold and not counted. */
function end(s: Shown): void {
  if (s.told) {
    const visible = s.since !== null ? state.now() - s.since : 0;
    record("page_left", { route: s.route, visible_ms: Math.min(MAX_VISIBLE_MS, Math.round(visible)), ...stepOf(s) });
  }
  s.told = false;
  s.since = null;
}

/** The page now in front of them: `null` when it is one that is not recorded
 *  (`/billing`, `/logout`) or no Recorder is left to say. The same page again
 *  is nothing — a Recorder mounted twice, a query that changed. */
export function show(route: string | null, step?: number): void {
  const was = doc.shown;
  if (was?.route === route && was?.step === step) return;
  if (was) end(was);
  doc.shown = null;
  if (route === null) return;
  doc.shown = { route, step, told: false, arrival: arrived(route), since: inFront() ? state.now() : null };
  tell(doc.shown, "hard");
}

/** The recorded page in front of them, if there is one: on any other,
 *  nothing is told. */
export function showing(): string | null {
  return doc.shown?.route ?? null;
}

/** The tab was hidden, or the page is going: the showing ends, and what is
 *  queued goes now, with the browser's promise to deliver it. */
export function hide(): void {
  if (doc.shown) end(doc.shown);
  void flush(true);
}

/** The tab is in front of them again: the page they come back to is shown
 *  anew. Already being shown, it is nothing; still hidden (a sign-out that
 *  failed while they were in another tab), it waits for the tab to be. */
export function unhide(): void {
  const s = doc.shown;
  if (!s || !inFront()) return;
  if (s.since === null) s.since = state.now();
  tell(s, "return");
}

/** Brought back from the back-forward cache: whoever is signed in now is in
 *  front of it (the server says who), so a sign-out that held the record
 *  before the page went lets go, and the page is shown again. */
export function restored(): void {
  state.held = false;
  unhide();
}

/** They agreed while a page is open: that page is the first one told. */
export function agreed(): void {
  if (doc.shown) tell(doc.shown, "hard");
}

/** A Recorder is mounted; what it returns is its going. The page is left
 *  only once none is mounted, a moment later: a Recorder that StrictMode
 *  mounts again, or the next layout's, arrives in the same commit. */
export function attach(): () => void {
  doc.recorders += 1;
  let gone = false;
  return () => {
    if (gone) return;
    gone = true;
    doc.recorders -= 1;
    queueMicrotask(() => {
      if (doc.recorders > 0) return;
      show(null);
      void flush(false);
    });
  };
}

/** They are signing out (components/auth/leave.ts): the page is left now, and
 *  what waits is delivered while the request still says who they are — after
 *  the sign-out it would be nobody's. Nothing more is taken until the page is
 *  gone or the sign-out fails (`stayed`). Settles once it is delivered, or
 *  after `HAND_OVER_MS` if the request hangs: a record never keeps anyone
 *  signed in who asked to leave. (So hung, it may reach the server after the
 *  sign-out and be filed as nobody's — the old way, now only when it hangs.) */
export async function handOver(): Promise<void> {
  if (doc.shown) end(doc.shown);
  const sent = flush(false);
  state.held = true;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const late = new Promise<void>((resolve) => { timer = setTimeout(resolve, HAND_OVER_MS); });
  await Promise.race([sent, late]);
  clearTimeout(timer);
}

/** The sign-out did not happen: they are still on the page, shown anew. */
export function stayed(): void {
  state.held = false;
  unhide();
}

/** Tests only: a fresh document. */
export function forgetDocument(): void {
  arrivals.count = 0;
  arrivals.last = null;
  arrivals.backAt = 0;
  doc.shown = null;
  doc.recorders = 0;
  state.held = false;
}

/** A command as a word for `client_error.code`: its leading lower-case
 *  parts only — `change:accept:41` is `change:accept`. The number is a row of
 *  theirs, and a record never names one (review of PR2, warning 7). */
export function commandOf(cmd: string | null | undefined): string | undefined {
  const parts: string[] = [];
  for (const p of (cmd ?? "").split(":")) {
    if (!/^[a-z_]+$/.test(p)) break;
    parts.push(p);
  }
  return parts.length ? parts.join(":") : undefined;
}

/** A control's `data-track` of surface.action[.target], as the event, or null
 *  for a value that is not three words at most. */
export function actionOf(value: string | null | undefined): { surface: string; action: string; target?: string } | null {
  const parts = (value ?? "").split(".");
  if (parts.length < 2 || parts.length > 3 || !parts.every((p) => WORD.test(p) && !p.includes("."))) return null;
  return { surface: parts[0], action: parts[1], ...(parts[2] ? { target: parts[2] } : {}) };
}
